import type { Resource } from '@/db/types'
import { compact, conflict, invalid } from '@/kinds/kind'
import { formatTime } from '@/lib/time'
import { MAX_ITEMS, type ListItem, type ListSpec, type ListStatus } from './schema'

export type ListResource = Resource<ListSpec, ListStatus>

/** Mirrors internal/kinds/list Defaults: style, pinned, items with ids and done flags. */
export const defaults = (spec: Partial<ListSpec>): ListSpec => ({
  title: spec.title ?? '',
  style: spec.style ?? 'checklist',
  pinned: spec.pinned ?? false,
  ...spec,
  items: (spec.items ?? []).map((it) => ({
    ...it,
    id: it.id || crypto.randomUUID(),
    done: it.done ?? false,
  })),
})

export const validate = (r: ListResource): string[] => {
  const spec = r.spec
  const errs: string[] = []
  if (!spec.title.trim()) errs.push('title is required')
  if (spec.items.length > MAX_ITEMS) errs.push('too many items')
  const seen = new Set<string>()
  for (const it of spec.items) {
    if (!it.text.trim()) errs.push('an item needs text')
    if (seen.has(it.id)) errs.push(`duplicate item id ${it.id}`)
    seen.add(it.id)
  }
  return errs
}

export const initialStatus = (r: { created_at: string }): ListStatus => ({
  last_touched: formatTime(new Date(r.created_at)),
})

export const displayName = (r: ListResource): string => r.spec.title.trim() || 'Untitled list'

export const afterPatch = (r: ListResource, at: string): ListResource => ({
  ...r,
  status: compact({ ...r.status, last_touched: formatTime(new Date(at)) }),
})

type Args = {
  id?: string
  text?: string
  before?: string | null
  items?: Partial<ListItem>[]
  remind_at?: string
}

/** Mirrors internal/kinds/list Apply. Fixtures: testdata/actions/list_*.json. */
export const apply = (r: ListResource, action: string, rawArgs: unknown, at: string): ListResource => {
  const a = (rawArgs ?? {}) as Args
  let items = r.spec.items.map((it) => ({ ...it }))
  const find = (id: string | undefined) => items.findIndex((it) => it.id === id)
  const gone = () => conflict('item is no longer on the list')
  switch (action) {
    case 'add_items': {
      if (!a.items?.length) throw invalid('add_items: items required')
      const added: ListItem[] = []
      for (const it of a.items) {
        const text = (it.text ?? '').trim()
        if (!text || !it.id) throw invalid('add_items: every item needs an id and text')
        if (find(it.id) >= 0) continue
        added.push({ id: it.id, text, done: false, ...(it.added_by ? { added_by: it.added_by } : {}) })
      }
      if (items.length + added.length > MAX_ITEMS) throw invalid('too many items')
      let pos = items.length
      if (a.before != null) {
        const i = find(a.before)
        if (i >= 0) pos = i
      }
      items.splice(pos, 0, ...added)
      break
    }
    case 'tick':
    case 'untick': {
      const i = find(a.id)
      if (i < 0) throw gone()
      if (action === 'tick') items[i] = { ...items[i]!, done: true, done_at: formatTime(new Date(at)) }
      else {
        const { done_at: _dropped, ...rest } = items[i]!
        items[i] = { ...rest, done: false }
      }
      break
    }
    case 'edit_item': {
      const i = find(a.id)
      if (i < 0) throw gone()
      const text = (a.text ?? '').trim()
      if (!text) throw invalid('edit_item: text required')
      items[i] = { ...items[i]!, text }
      break
    }
    case 'remove_item': {
      const i = find(a.id)
      if (i < 0) throw gone()
      items.splice(i, 1)
      break
    }
    case 'move_item': {
      const i = find(a.id)
      if (i < 0) throw gone()
      const [it] = items.splice(i, 1)
      let pos = items.length
      if (a.before != null) {
        const j = items.findIndex((x) => x.id === a.before)
        if (j >= 0) pos = j
      }
      items.splice(pos, 0, it!)
      break
    }
    case 'set_item_reminder': {
      const i = find(a.id)
      if (i < 0) throw gone()
      const when = a.remind_at ?? ''
      if (when && Number.isNaN(Date.parse(when)))
        throw invalid('set_item_reminder: remind_at must be RFC 3339')
      if (when) items[i] = { ...items[i]!, remind_at: when }
      else {
        const { remind_at: _dropped, ...rest } = items[i]!
        items[i] = rest
      }
      break
    }
    case 'clear_ticked':
      items = items.filter((it) => !it.done)
      break
    case 'reset':
      items = items.map((it) => {
        const { done_at: _dropped, ...rest } = it
        return { ...rest, done: false }
      })
      break
    case 'convert': {
      if (r.status.converted_to) throw conflict('list already became something else')
      const ref = a as { kind?: unknown; id?: unknown }
      if (typeof ref.kind !== 'string' || !ref.kind || typeof ref.id !== 'string' || !ref.id)
        throw invalid('convert: kind and id required')
      return {
        ...r,
        status: compact({
          ...r.status,
          converted_to: { kind: ref.kind, id: ref.id },
          last_touched: formatTime(new Date(at)),
        }),
      }
    }
    default:
      throw invalid(`unknown action ${action}`)
  }
  return afterPatch({ ...r, spec: { ...r.spec, items } }, at)
}
